"use client";

import { useEffect, useState } from "react";
import { useNavigation } from "@remix-run/react";
import { Progress } from "~/components/ui/progress";
import { cn } from "~/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { Loader2 } from "lucide-react";

export function RouteProgressBar() {
  const [progress, setProgress] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const navigation = useNavigation();
  const isRouteChanging = navigation.state !== "idle";

  useEffect(() => {
    let timer: NodeJS.Timeout;
    let tooltipTimer: NodeJS.Timeout;

    const startProgress = () => {
      setProgress(0);
      setShowTooltip(false);
      timer = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 90) {
            clearInterval(timer);
            return prevProgress;
          }
          return prevProgress + 4;
        });
      }, 500);

      tooltipTimer = setTimeout(() => {
        setShowTooltip(true);
      }, 1000);
    };

    const stopProgress = () => {
      clearInterval(timer);
      clearTimeout(tooltipTimer);
      setProgress(100);
      setShowTooltip(false);
      setTimeout(() => {
        setProgress(0);
      }, 300);
    };

    if (isRouteChanging) {
      startProgress();
    } else {
      stopProgress();
    }

    return () => {
      clearInterval(timer);
      clearTimeout(tooltipTimer);
    };
  }, [isRouteChanging]);

  return (
    <>
      <Progress
        value={progress}
        className={cn(
          `w-full bg-transparent transition-all duration-500 h-1`,
          !isRouteChanging ? "opacity-0" : "opacity-100"
        )}
      />
      {showTooltip && (
        <TooltipProvider>
          <Tooltip open={showTooltip}>
            <TooltipTrigger asChild>
              <div className="fixed top-12 lg:top-14 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
                <span className="sr-only">Loading</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span>Generating page with latest data</span>
                </div>
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  This can take a few seconds
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </>
  );
}
